import { useTRPC } from "@nwga/features/client";
import { Button } from "@nwga/ui/components/button";
import { Field } from "@nwga/ui/components/field";
import { Input } from "@nwga/ui/components/input";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import * as React from "react";
import { z } from "zod";
import { AuthCard } from "../components/auth-card.tsx";
import { SocialSignIn } from "../components/social-sign-in.tsx";
import { captchaHeaders, useTurnstile } from "../components/turnstile.tsx";
import { signIn } from "../lib/auth.ts";

export const Route = createFileRoute("/sign-in")({
	validateSearch: z.object({
		/** Where to return to after signing in. */
		redirect: z.string().optional(),
	}),
	component: SignInPage,
});

function SignInPage() {
	const { redirect } = Route.useSearch();
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const trpc = useTRPC();
	const session = useQuery(trpc.session.queryOptions());

	const [error, setError] = React.useState<string | null>(null);
	const [pending, setPending] = React.useState(false);
	const turnstile = useTurnstile();

	const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
		event.preventDefault();
		setError(null);
		setPending(true);

		const data = new FormData(event.currentTarget);
		const result = await signIn.email({
			email: String(data.get("email")),
			password: String(data.get("password")),
			fetchOptions: { headers: captchaHeaders(turnstile.token) },
		});

		setPending(false);

		if (result.error !== null && result.error !== undefined) {
			// Turnstile tokens are single-use, so the one just spent is no good for
			// the retry this error invites. Without the reset, a mistyped password
			// fails twice: once for the password, once for a replayed token.
			turnstile.reset();
			// Deliberately not distinguishing "no such account" from "wrong
			// password": either message tells an attacker whether an address is
			// registered.
			setError("That email and password did not match.");
			return;
		}

		// The session query is now stale for every consumer, including the header.
		await queryClient.invalidateQueries();
		// `/signed-in` decides where this account belongs — the console for staff,
		// the business dashboard for everyone else. See `landingFor`.
		await navigate({ to: redirect ?? "/signed-in", replace: true });
	};

	return (
		<AuthCard
			title="Business Sign In"
			description="Post job listings for your business."
			footer={
				<p>
					No account yet?{" "}
					<Link
						to="/sign-up"
						className="font-medium text-foreground underline-offset-4 hover:underline"
					>
						Create one
					</Link>
				</p>
			}
		>
			<SocialSignIn
				providers={session.data?.socialProviders ?? []}
				callbackURL={redirect ?? "/signed-in"}
			/>

			<form
				className="mt-4 space-y-4"
				onSubmit={(event) => void handleSubmit(event)}
			>
				<Field label="Email" required>
					{(props) => (
						<Input
							{...props}
							name="email"
							type="email"
							autoComplete="email"
							required
						/>
					)}
				</Field>

				<Field
					label="Password"
					required
					errors={error === null ? undefined : [error]}
				>
					{(props) => (
						<Input
							{...props}
							name="password"
							type="password"
							autoComplete="current-password"
							required
						/>
					)}
				</Field>

				<div className="text-right">
					<Link
						to="/forgot-password"
						className="text-muted-foreground text-sm underline-offset-4 hover:text-foreground hover:underline"
					>
						Forgot your password?
					</Link>
				</div>

				{turnstile.widget}

				<Button
					type="submit"
					size="lg"
					className="w-full"
					disabled={pending || turnstile.pending}
				>
					{pending ? "Signing in…" : "Sign in"}
				</Button>
			</form>
		</AuthCard>
	);
}
